import { PARENT_TYPES, ROOT_TYPES, SourceTypes } from "@/types/containerTypes";
import { CalloutOperationTypes, GraphParams, OperationInterface, operationTypes, OperationParams } from "@/types/operationTypes";
import { Code } from "./code";
import { Commanding } from "./commanding";

export class Operation implements OperationInterface {
  id: string;
  content: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  type: operationTypes;
  parentId: string;
  position: number;
  title: string;
  isScaffold: boolean;
  sourceId: string;
  sourceType?: SourceTypes;
  inputConstraint: string;
  code?: Code;
  commanding?: Commanding;
  graph?: GraphParams;
  rangeParameterId: string;
  skipParameterId: string;
  skipKeyId: string;
  rootId: string;
  rootType: ROOT_TYPES;
  parentType: PARENT_TYPES;
  equipmentId?: string;
  collectedFileName: string;
  propelOrgId: string;

  constructor(params: OperationParams) {
    this.id = params.id;
    this.updatedAt = params.updatedAt;
    this.createdAt = params.createdAt;
    this.type = params.type;
    this.parentId = params.parentId;
    this.content = params.content || '';
    this.position = params.position || 0;
    this.title = params.title || '';
    this.isScaffold = params.isScaffold;
    this.sourceId = params.sourceId || '';
    this.sourceType = params.sourceType;
    this.inputConstraint = params.inputConstraint || '';
    this.code = params.code ? new Code(params.code) : undefined;
    this.commanding = params.commanding ? new Commanding(params.commanding) : undefined;
    this.graph = params.graph;
    this.rangeParameterId = params.rangeParameterId || '';
    this.skipParameterId = params.skipParameterId || '';
    this.skipKeyId = params.skipKeyId || '';
    this.rootId = params.rootId;
    this.rootType = params.rootType;
    this.parentType = params.parentType;
    this.equipmentId = params.equipmentId;
    this.collectedFileName = params.collectedFileName || '';
    this.propelOrgId = params.propelOrgId || '';
  }

  isFileType(): boolean {
    return this.isFile() || this.isFileInput();
  }

  isText(): boolean {
    return this.type === operationTypes.Text || this.isCallout();
  }

  isCallout(): boolean {
    return CalloutOperationTypes.includes(this.type);
  }

  isFieldInput(): boolean {
    return this.type === operationTypes.FieldInput;
  }

  isField(): boolean {
    return this.type === operationTypes.Field;
  }

  isFieldType(): boolean {
    return this.isField() || this.isFieldInput();
  }

  isFileInput(): boolean {
    return this.type === operationTypes.FileInput;
  }

  isFile(): boolean {
    return this.type === operationTypes.File;
  }

  // Procedure authors can name the file this operation collects instead of
  // inheriting whatever name the provider wrote to disk.
  hasCollectedFileName(): boolean {
    return this.isFile() && this.collectedFileName.trim() !== '';
  }

  isGraph(): boolean {
    return this.type === operationTypes.Grapher;
  }

  isTable(): boolean {
    return this.type === operationTypes.Table;
  }

  hasParameter(): boolean {
    return this.isField() || this.isFieldInput() || this.isFileType() || this.isTable() || this.isGraph();
  }

  isCode(): boolean {
    return this.type === operationTypes.Code;
  }

  isCommanding(): boolean {
    return this.type === operationTypes.Commanding;
  }

  isInput(): boolean {
    return this.isFieldInput() || this.isFileInput();
  }

  isNotes(): boolean {
    return this.type === operationTypes.Notes;
  }

  isEquipment(): boolean {
    return this.type === operationTypes.Equipment;
  }

  // operations that can have their value interpolated into text (i.e. commandings or text operations) can have identifiers
  hasIdentifier(): boolean {
    return this.isField() || this.isFieldInput() || this.isFileType();
  }
}
